import { type FC, useMemo } from "react";

import {
  Column,
  Row,
  Tooltip,
  Text,
  Box,
  Popover,
  PopoverContent,
  Portal,
  TextWithTooltip,
} from "@hightouch/htui";
import moment from "moment";
import pluralize from "pluralize";
import type { ElementOf } from "ts-essentials";

import { Circle } from "src/ui/circle";
import { WEEK_DAYS } from "src/utils/constants";

import {
  CALENDAR_RECURRENCE_SCHEDULE_TYPE,
  type ReportSchedule,
  type Schedule as ScheduleObject,
  ScheduleType,
  type ScheduleExpression,
  type ScheduleInterval,
} from "./types";
import {
  calendarScheduleToMonthlyPickerState,
  formatMonthlyScheduleSummary,
} from "./calendar-recurrence";
import { UTCDayToLocalDay } from "./visual-cron-expression";
import { Link } from "src/router";
import type { LatestJourneyVersionFromDestinationInstanceIdQuery } from "src/graphql";
import { describeWhenSyncRuns } from "src/utils/syncs";
import { PopoverTriggerButton } from "src/ui/popover";

type ScheduleProps = {
  schedule: ScheduleObject | undefined;
};

export const Schedule: FC<Readonly<ScheduleProps>> = ({ schedule }) => {
  if (!schedule) {
    return <Manual />;
  }

  const type = schedule.type;
  switch (type) {
    case ScheduleType.DBT_CLOUD:
      return (
        <DBT
          account={schedule.schedule?.account?.name}
          job={schedule.schedule?.job?.name}
        />
      );
    case ScheduleType.INTERVAL:
      return <Interval interval={schedule.schedule?.interval} />;
    case ScheduleType.CRON:
      return <Cron expression={schedule.schedule?.expression} />;
    case ScheduleType.CUSTOM:
      return <VisualCron expressions={schedule.schedule?.expressions ?? []} />;
    case ScheduleType.MANUAL:
      return <Manual />;
    case ScheduleType.FIVETRAN:
      return <Text fontWeight="medium">Fivetran</Text>;
    case ScheduleType.STREAMING:
      return <Text fontWeight="medium">Streaming</Text>;
    case ScheduleType.MATCH_BOOSTER:
      return <Text fontWeight="medium">Match Booster</Text>;
    case ScheduleType.JOURNEY_TRIGGERED:
      return <Text fontWeight="medium">Journey-triggered</Text>;
    case ScheduleType.SEQUENCE:
      return <Text fontWeight="medium">Sequence</Text>;
    case ScheduleType.IDENTITY_RESOLUTION:
      return <Text fontWeight="medium">Identity Resolution</Text>;
    default:
      throw new Error(`Unknown type: ${type satisfies never}`);
  }
};

interface VisualCronProps {
  expressions: ScheduleExpression[];
  popoverTriggerFlexDir?: "row" | "column";
  variant?: "light" | "dark";
}

export const VisualCron: FC<VisualCronProps> = ({
  expressions,
  popoverTriggerFlexDir = "row",
  variant = "light",
}) => {
  const expression = expressions[0];

  // Unexpected, but possible given the types so handle it gracefully
  if (!expression) {
    return null;
  }

  if (expressions.length === 1) {
    return <VisualCronExpression expression={expression} variant={variant} />;
  }

  return (
    <Row gap={2} justifyContent="center" flexDir={popoverTriggerFlexDir}>
      <VisualCronExpression expression={expression} variant={variant} />
      <Popover placement="bottom" closeOnBlur={true} isLazy>
        <PopoverTriggerButton aria-label="View more">
          <Box as="span" color="primary.base" fontWeight="medium">
            +{expressions.length - 1} more
          </Box>
        </PopoverTriggerButton>
        <Portal>
          <Box
            sx={{
              "& .chakra-popover__popper": {
                zIndex: "popover",
              },
            }}
          >
            <PopoverContent width="max-content">
              <Column gap={2} p={3}>
                {expressions.slice(1).map((expression, index) => (
                  <VisualCronExpression
                    expression={expression}
                    key={index}
                    variant={variant}
                  />
                ))}
              </Column>
            </PopoverContent>
          </Box>
        </Portal>
      </Popover>
    </Row>
  );
};

interface VisualCronExpressionProps {
  expression: ScheduleExpression;
  variant: "light" | "dark";
}

const VisualCronExpression: FC<VisualCronExpressionProps> = ({
  expression,
  variant,
}) => {
  const selectedDays = useMemo(() => {
    return Object.keys(expression?.days ?? {}).map((day) => {
      return UTCDayToLocalDay(
        expression.time,
        Number(moment(day, "dddd").format("e")),
      );
    });
  }, [expression]);

  if (Object.keys(expression?.days || {}).length === 7) {
    return (
      <Text color={variant === "light" ? "black" : "white"}>
        Every day at{" "}
        {moment.utc(expression?.time, "HH:mm").local().format("h:mm A")}
      </Text>
    );
  }

  return (
    <Row gap={1} align="center">
      {WEEK_DAYS.map((day, index) => {
        const isSelected = selectedDays.includes(index);
        const colors = {
          light: {
            bg: isSelected ? "teal.200" : undefined,
            borderColor: isSelected ? "teal.400" : "gray.200",
            color: isSelected ? "teal.800" : "text.primary",
          },
          dark: {
            bg: isSelected ? "primary.base" : undefined,
            borderColor: isSelected ? "primary.base" : "gray.base",
            color: "white",
          },
        };

        return (
          <Tooltip key={index} message={day}>
            <Circle
              radius="20px"
              bg={colors[variant].bg}
              borderWidth="hairline"
              borderStyle="solid"
              borderColor={colors[variant].borderColor}
              color={colors[variant].color}
            >
              <Text size="sm" color="inherit" fontWeight="medium">
                {day[0]}
              </Text>
            </Circle>
          </Tooltip>
        );
      })}

      <Text ml={1} color={variant === "light" ? "text.secondary" : "white"}>
        at {moment.utc(expression?.time, "HH:mm").local().format("h:mm A")}
      </Text>
    </Row>
  );
};

interface CronProps {
  expression: string | undefined;
}

export const Cron: FC<CronProps> = ({ expression }) => (
  <Text fontWeight="medium">{expression}</Text>
);

interface IntervalProps {
  interval: ScheduleInterval | undefined;
}

export const Interval: FC<IntervalProps> = ({ interval }) => {
  if (interval?.unit != null && interval.quantity != null) {
    return (
      <Text fontWeight="medium">
        Every {pluralize(interval?.unit, interval?.quantity, true)}
      </Text>
    );
  }

  return null;
};

export const Manual: FC = () => <Text fontWeight="medium">Manual</Text>;

interface DbtProps {
  job: string | undefined;
  account: string | undefined;
}

export const DBT: FC<DbtProps> = ({ job, account }) => (
  <Text fontWeight="medium">
    <strong>dbt Cloud</strong> ({account} - {job})
  </Text>
);

export const ScheduleDisplay = ({
  schedule,
  journey,
  variant = "light",
}: {
  schedule: ReportSchedule | null;
  journey?: ElementOf<
    LatestJourneyVersionFromDestinationInstanceIdQuery["journeys"]
  >;
  variant?: "light" | "dark";
}) => {
  if (schedule?.type === CALENDAR_RECURRENCE_SCHEDULE_TYPE) {
    return (
      <Text fontWeight="medium">
        {formatMonthlyScheduleSummary(
          calendarScheduleToMonthlyPickerState(schedule),
        )}
      </Text>
    );
  }

  if (schedule?.type === ScheduleType.CUSTOM) {
    const expressions = schedule.schedule?.expressions ?? [];

    // By default, the `VisualCron` component will roll up multiple expressions into a +X more popover.
    // We don't want that behavior here, so instead just render each expression individually.
    return (
      <Column gap={2}>
        {expressions.map((expression, index) => (
          <VisualCron
            key={index}
            expressions={[expression]}
            variant={variant}
          />
        ))}
      </Column>
    );
  }

  if (schedule?.type === ScheduleType.JOURNEY_TRIGGERED && journey) {
    return (
      <Text color={variant === "light" ? "black" : "white"}>
        Runs as part of a journey (
        <Link href={`/journeys/${journey.id}`}>{journey.name}</Link>)
      </Text>
    );
  }

  return <TextWithTooltip>{describeWhenSyncRuns(schedule)}</TextWithTooltip>;
};
